/**
 * Row-interaction helpers for `CartContainer` + `EntityList` rows (CT-5187).
 *
 * Duplicated from
 * `aims-os-governance/src/domains/truth-plane/utils/truthPlaneEntityListHandlers.js`
 * rather than imported: cross-app imports are forbidden, and Governance ships as
 * `@aimsos/governance` which we must not depend on.
 *
 * Without these, clicking the kebab menu ALSO opens the row's detail panel —
 * the menu lives inside a clickable `CartContainer`, so its click bubbles.
 */

/** Selectors for controls that must not bubble to the {@link CartContainer} row click. */
export const ENTITY_LIST_INTERACTIVE_SELECTOR = [
  'button',
  '[role="menu"]',
  '[role="menuitem"]',
  '[role="checkbox"]',
  'a',
  'input',
  'select',
  'textarea',
].join(', ');

/** Shared {@link Dropdown} props for EntityList kebab menus (portal, right-aligned). */
export const WIDGET_ENTITY_LIST_DROPDOWN_PROPS = {
  align: 'right',
  usePortal: true,
};

/**
 * Pass as `onClick` on {@link EntityList} when the row sits inside a clickable
 * {@link CartContainer}.
 */
export function stopEntityListInteractivePropagation(event) {
  if (event.target.closest(ENTITY_LIST_INTERACTIVE_SELECTOR)) {
    event.stopPropagation();
  }
}

/** Wrap dropdown/menu handlers so choosing an action does not open the row preview. */
export function wrapEntityListMenuAction(handler) {
  if (typeof handler !== 'function') return handler;
  return (event) => {
    event?.stopPropagation?.();
    handler(event);
  };
}

/**
 * Pass as `onKeyDown` alongside {@link stopEntityListInteractivePropagation}.
 *
 * `CartContainer` is `role="button"` and its own `onKeyDown` treats **any**
 * Enter or Space reaching it as a row click — including one pressed while focus
 * is on the kebab or a CTA inside the row. It also calls `preventDefault`, so
 * the focused control never activates and the row preview opens instead.
 *
 * Stopping propagation alone is not enough: React forwards it to the native
 * event, and the browser then skips the button's own activation too, leaving
 * the control dead to the keyboard. So for a button or a link the activation is
 * driven here — `preventDefault` + `click()` — which fires it exactly once
 * instead of racing the browser's default.
 *
 * Other interactive descendants (inputs, selects, textareas) keep their native
 * Enter behaviour; only the row's handler is taken out of the path.
 */
export function stopEntityListInteractiveKeyDown(event) {
  if (event.key !== 'Enter' && event.key !== ' ') return;

  const control = event.target.closest(ENTITY_LIST_INTERACTIVE_SELECTOR);
  if (!control) return;

  event.stopPropagation();

  if (control.tagName === 'BUTTON' || control.tagName === 'A') {
    event.preventDefault();
    control.click();
  }
}
